/*
 * -----------------------------------------------------------------------
 *  <copyright file="import-library-controls.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tabs } from '@mui/material';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import ExpandableGrid from '@components/core/expandable/grid';
import { ImportLibraryInstructionsProps, ImportLibraryProps } from '../interfaces/import-library.interface';
import { useTranslation } from '@hooks/index';

const TabContainer = ({ children }: any) => (
    <Typography component="div" style={{ padding: 24 }}>
        {children}
    </Typography>
);

export const ImportLibraryControls = ({ data, lovs, isSystemLevel }: ImportLibraryProps) => {
    const { t } = useTranslation();
    if (!data || !data.length) return <div>{t('ra:importLibraryControlsNoData')}</div>;

    return (
        <>
            <div className="secondary" style={{ fontSize: 14, marginBottom: 20 }}>
                {t('ra:confirmImportedLibraryControls', { count: data.length })}
            </div>
            <ExpandableGrid
                customKey="imported-controls-grid"
                id="imported-controls"
                key="imported-controls-grid"
                items={data}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                removeHideFilter
                pageable={false}
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                columns={[
                    ...(!isSystemLevel
                        ? [
                              {
                                  field: 'controlPhaseId',
                                  label: t('ra:phase'),
                                  width: 250,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>{lovs?.find((lov: any) => lov.id === dataItem.controlPhaseId)?.name}</td>
                                      ),
                                  },
                              },
                              {
                                  field: 'controlTypeId',
                                  label: t('ra:controlType'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>{lovs?.find((lov: any) => lov.id === dataItem.controlTypeId)?.name}</td>
                                      ),
                                  },
                              },
                          ]
                        : []),
                    {
                        field: 'description',
                        label: t('ra:description'),
                        width: 350,
                    },
                    {
                        field: 'action',
                        label: t('ra:actionToBeTaken'),
                        width: 350,
                    },
                    {
                        field: 'threshold',
                        label: t('ra:threshold'),
                        width: 350,
                    },
                    ...(!isSystemLevel
                        ? [
                              {
                                  field: 'responsibleFunctionId',
                                  label: t('ra:responsibleFunction'),
                                  width: 220,
                                  cells: {
                                      data: ({ dataItem }: any) => (
                                          <td>
                                              {
                                                  lovs?.find((lov: any) => lov.id === dataItem.responsibleFunctionId)
                                                      ?.name
                                              }
                                          </td>
                                      ),
                                  },
                              },
                          ]
                        : []),

                    {
                        field: 'reviewFrequencyPeriod',
                        label: t('ra:reviewFrequencyUnit'),
                        width: 250,
                    },
                    {
                        field: 'reviewFrequencyUnits',
                        label: t('ra:reviewFrequencyPeriod'),
                        width: 250,
                    },
                ]}
                showFirstColumn={false}
            />
        </>
    );
};

export const ImportLibraryControlsInstructions = ({ isSystemLevel }: ImportLibraryInstructionsProps) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(0);

    return (
        <>
            <div className="secondary" style={{ fontSize: 14, marginBottom: 15 }}>
                {t('ra:importLibraryControlsInstructions')}
            </div>
            <Grid
                style={{ fontSize: 11 }}
                data={[
                    ...(isSystemLevel
                        ? [
                              {
                                  number: 1,
                                  field: t('ra:description'),
                                  type: 'Text',
                                  required: t('yes'),
                                  maxLength: 3000,
                                  notes: t('ra:n/a'),
                              },
                              {
                                  number: 2,
                                  field: t('ra:actionToBeTaken'),
                                  type: 'Text',
                                  required: t('no'),
                                  maxLength: 3000,
                                  notes: t('ra:n/a'),
                              },
                              {
                                  number: 3,
                                  field: t('ra:threshold'),
                                  type: 'Text',
                                  required: t('no'),
                                  maxLength: 3000,
                                  notes: t('ra:n/a'),
                              },
                              {
                                  number: 4,
                                  field: t('ra:reviewFrequencyUnit'),
                                  required: t('no/yes'),
                                  maxLength: t('ra:n/a'),
                                  type: 'Number',
                                  notes: t('ra:reviewFrequencyUnitNotes'),
                              },
                              {
                                  number: 5,
                                  field: t('ra:reviewFrequencyPeriod'),
                                  required: t('no/yes'),
                                  maxLength: t('ra:n/a'),
                                  type: 'Text',
                                  notes: t('ra:reviewFrequencyPeriodNotes'),
                              },
                          ]
                        : [
                              {
                                  number: 1,
                                  field: t('ra:phase'),
                                  type: 'Text',
                                  maxLength: t('ra:n/a'),
                                  required: t('no'),
                                  notes: t('ra:activeLovForOrg'),
                              },
                              {
                                  number: 2,
                                  field: t('ra:type'),
                                  required: t('no'),
                                  maxLength: 50,
                                  type: 'Text',
                                  notes: t('ra:activeLovForOrg'),
                              },
                              {
                                  number: 3,
                                  field: t('ra:description'),
                                  type: 'Text',
                                  required: t('yes'),
                                  maxLength: 3000,
                                  notes: t('ra:n/a'),
                              },
                              {
                                  number: 4,
                                  field: t('ra:actionToBeTaken'),
                                  type: 'Text',
                                  required: t('no'),
                                  maxLength: 3000,
                                  notes: t('ra:n/a'),
                              },
                              {
                                  number: 5,
                                  field: t('ra:threshold'),
                                  type: 'Text',
                                  required: t('no'),
                                  maxLength: 3000,
                                  notes: t('ra:n/a'),
                              },
                              {
                                  number: 6,
                                  field: t('ra:responsibleFunction'),
                                  required: t('no'),
                                  maxLength: 50,
                                  type: 'Text',
                                  notes: t('ra:activeLovForOrg'),
                              },
                              {
                                  number: 7,
                                  field: t('ra:reviewFrequencyUnit'),
                                  required: t('no/yes'),
                                  maxLength: t('ra:n/a'),
                                  type: 'Number',
                                  notes: t('ra:reviewFrequencyUnitNotes'),
                              },
                              {
                                  number: 8,
                                  field: t('ra:reviewFrequencyPeriod'),
                                  required: t('no/yes'),
                                  maxLength: t('ra:n/a'),
                                  type: 'Text',
                                  notes: t('ra:reviewFrequencyPeriodNotes'),
                              },
                          ]),
                ]}
            >
                <Column title={t('ra:columnNum')} field="number" width={120} />
                <Column title={t('ra:field')} field="field" width={120} />
                <Column title={t('ra:fieldType')} field="type" width={120} />
                <Column title={t('ra:maxLength')} field="maxLength" width={120} />
                <Column title={t('ra:required')} field="required" width={100} />
                <Column title={t('ra:notes')} field="notes" />
            </Grid>
            <h3>{t('ra:Examples')}:</h3>
            <Tabs
                value={value}
                onChange={(e, newValue) => setValue(newValue)}
                indicatorColor="primary"
                textColor="primary"
                centered
            >
                <Tab label="XLSX" icon={<FontAwesomeIcon icon={['fal', 'file-excel']} />} data-testid="xlsx-tab" />
                <Tab label="CSV" icon={<FontAwesomeIcon icon={['fal', 'file-csv']} />} data-testid="csv-tab" />
                <Tab label="PSV" icon={<FontAwesomeIcon icon={['fal', 'file-spreadsheet']} />} data-testid="psv-tab" />
            </Tabs>
            {value === 0 && (
                <TabContainer>
                    <img
                        src={
                            isSystemLevel
                                ? '/img/import-library-controls-example-xlsx.png'
                                : '/img/import-library-org-controls-example-xlsx.PNG'
                        }
                        alt="XLSX"
                        className="import-image-example"
                    />
                </TabContainer>
            )}
            {value === 1 && (
                <TabContainer>
                    <img
                        src={
                            isSystemLevel
                                ? '/img/import-library-controls-example-csv.png'
                                : '/img/import-library-org-controls-example-csv.PNG'
                        }
                        alt="CSV"
                        className="import-image-example"
                    />
                </TabContainer>
            )}
            {value === 2 && (
                <TabContainer>
                    <img
                        src={
                            isSystemLevel
                                ? '/img/import-library-controls-example-psv.png'
                                : '/img/import-library-org-controls-example-psv.PNG'
                        }
                        alt="PSV"
                        className="import-image-example"
                    />
                </TabContainer>
            )}
        </>
    );
};
